import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
    faCircleCheck,
    faGripVertical,
    faPen,
    faTrash,
    faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { MenuBarComponent } from '../../core/components/menu-bar/menu-bar.component';
import { FooterComponent } from '../../core/components/footer/footer.component';
import { AdminNavComponent } from '../../core/components/admin-nav/admin-nav.component';
import { ProjectModalComponent } from '../../core/components/project-modal/project-modal.component';
import { ConfirmModalComponent } from '../../core/components/confirm-modal/confirm-modal.component';
import { AdminProject, AdminProjectsService, ProjectPayload } from '../../core/services/admin-projects.service';
import { LanguageService } from '../../core/services/language.service';

/**
 * Admin editor for Fyes_Projects — the case studies in the home page's
 * "Successful projects" carousel and on the solution pages.
 *
 * Rows are listed in carousel order so the table reads the way the site does.
 */
@Component({
    selector: 'app-admin-projects',
    imports: [CommonModule, FormsModule, DragDropModule, MatTooltipModule, FontAwesomeModule, MenuBarComponent,
        FooterComponent, AdminNavComponent],
    templateUrl: './admin-projects.component.html',
    styleUrl: './admin-projects.component.scss',
})
export class AdminProjectsComponent implements OnInit {
    readonly faPen = faPen;
    readonly faTrash = faTrash;
    readonly faGripVertical = faGripVertical;
    readonly faCircleCheck = faCircleCheck;
    readonly faTriangleExclamation = faTriangleExclamation;

    projects: AdminProject[] = [];
    loading = true;
    search = '';
    /** True while a dragged order is being saved, so a second drag cannot race
     *  the first. */
    savingOrder = false;
    /** The row whose cell widths were pinned for the drag preview. */
    private lockedRow: HTMLElement | null = null;

    /** Rows whose thumbnail has finished loading (or failed), so the skeleton
     *  can hold the slot while a signed S3 URL is still in flight. */
    private settled = new Set<string>();
    private failedImages = new Set<string>();

    constructor(
        private projectsService: AdminProjectsService,
        private languageService: LanguageService,
        private modalService: NgbModal,
        private toastr: ToastrService,
    ) { }

    ngOnInit(): void {
        this.load();
    }

    get filtered(): AdminProject[] {
        const q = this.search.trim().toLowerCase();
        if (!q) return this.projects;
        return this.projects.filter((p) =>
            (p.title || '').toLowerCase().includes(q) ||
            (p.location || '').toLowerCase().includes(q) ||
            (p.industry || '').toLowerCase().includes(q));
    }

    /**
     * Dragging is only allowed on the unfiltered list.
     *
     * The new order is saved as "renumber 1..N by position", which needs every
     * project. Dropping a row inside a filtered subset would renumber from 1
     * using only the visible rows and scramble the ones hidden by the search.
     */
    get canReorder(): boolean {
        return this.search.trim() === '' && !this.savingOrder;
    }

    /**
     * Pin the row's column widths before CDK clones it.
     *
     * The preview is a detached copy of the <tr>, so it has no table to take
     * column widths from and lays its cells out by content instead. CDK sizes
     * the preview's outer box, but not the cells inside it — writing the current
     * widths onto the cells means the clone carries them and the preview matches
     * the row it came from.
     *
     * Runs on pointerdown, which is before CDK builds the preview on the first
     * move. The widths are what the browser already computed, so pinning them
     * changes nothing about how the live row looks.
     */
    lockRowWidths(event: Event): void {
        const row = (event.target as HTMLElement)?.closest('tr');
        if (!row) return;
        this.lockedRow = row;
        row.style.width = `${row.offsetWidth}px`;
        for (const cell of Array.from(row.children) as HTMLElement[]) {
            cell.style.width = `${cell.offsetWidth}px`;
        }
    }

    /** Hands the columns back to the table once the drag is over. */
    unlockRowWidths(): void {
        const row = this.lockedRow;
        this.lockedRow = null;
        if (!row) return;
        row.style.removeProperty('width');
        for (const cell of Array.from(row.children) as HTMLElement[]) {
            cell.style.removeProperty('width');
        }
    }

    /**
     * Persist a dragged order.
     *
     * The rows are moved locally first so the table does not jump while the
     * request is in flight; the response is authoritative and replaces them, and
     * a failure reloads to undo the optimistic move.
     */
    onDrop(event: CdkDragDrop<AdminProject[]>): void {
        // Released outside the list, or dropped back where it started.
        if (!this.canReorder || event.previousIndex === event.currentIndex) return;

        moveItemInArray(this.projects, event.previousIndex, event.currentIndex);
        // Show the numbers they are about to get, rather than the stale ones.
        this.projects.forEach((project, index) => (project.displayOrder = index + 1));

        this.savingOrder = true;
        this.projectsService.reorder(this.projects.map((p) => p.id)).subscribe({
            next: (res) => {
                this.savingOrder = false;
                if (res?.projects) this.projects = res.projects;
            },
            error: (err) => {
                this.savingOrder = false;
                this.toastr.error(this.messageFor(err, 'Unable to save the new order'));
                this.load();
            },
        });
    }

    /**
     * Languages the app offers that this project has no copy for at all.
     *
     * Those visitors see the English instead, which reads correctly but is worth
     * flagging. Driven by LanguageService rather than a hardcoded 'el', so a new
     * language shows up in this column on its own.
     */
    missingLanguages(project: AdminProject): string[] {
        return this.languageService.languages
            .filter((lang) => lang.code !== 'en')
            .filter((lang) => {
                const copy = project.translations?.[lang.code];
                if (!copy) return true;
                return !copy.title && !copy.description && !copy.problemStatement
                    && !copy.solution && !copy.industry && !copy.location;
            })
            .map((lang) => lang.label);
    }

    /**
     * Tooltip for the translation state icon.
     *
     * Names the languages rather than just saying "incomplete", and says what
     * the consequence is — a missing translation is not an error, those
     * visitors simply get the English.
     */
    translationTooltip(project: AdminProject): string {
        const missing = this.missingLanguages(project);
        if (!missing.length) return 'All translations are filled in';
        return `${missing.join(', ')} language translations are missing`
            + ' — those visitors see the English text';
    }

    load(): void {
        this.loading = true;
        // a re-fetch hands back freshly signed URLs, so the rows load again
        this.settled.clear();
        this.failedImages.clear();
        this.projectsService.list().subscribe({
            next: (res) => {
                this.projects = res?.projects ?? [];
                this.loading = false;
            },
            error: () => {
                this.projects = [];
                this.loading = false;
                this.toastr.error('Unable to load projects');
            },
        });
    }

    openAdd(): void {
        const ref = this.modalService.open(ProjectModalComponent, {
            centered: true,
            size: 'lg',
            windowClass: 'auth-modal-window',
        });
        ref.componentInstance.siblings = this.projects;
        ref.result.then(
            (payload: ProjectPayload) => {
                if (!payload) return;
                this.projectsService.create(payload).subscribe({
                    next: () => {
                        this.toastr.success(`${payload.title} created`);
                        this.load();
                    },
                    error: (err) => this.toastr.error(this.messageFor(err, 'Unable to create this project')),
                });
            },
            () => { /* dismissed */ },
        );
    }

    openEdit(project: AdminProject): void {
        const ref = this.modalService.open(ProjectModalComponent, {
            centered: true,
            size: 'lg',
            windowClass: 'auth-modal-window',
        });
        ref.componentInstance.project = project;
        ref.componentInstance.siblings = this.projects;
        ref.result.then(
            (payload: ProjectPayload) => {
                if (!payload) return;
                this.projectsService.update(project.id, payload).subscribe({
                    next: () => {
                        this.toastr.success(`${payload.title} updated`);
                        this.load();
                    },
                    error: (err) => this.toastr.error(this.messageFor(err, 'Unable to save this project')),
                });
            },
            () => { /* dismissed */ },
        );
    }

    remove(project: AdminProject): void {
        const ref = this.modalService.open(ConfirmModalComponent, {
            centered: true,
            windowClass: 'auth-modal-window',
        });
        ref.componentInstance.title = 'Delete project';
        ref.componentInstance.message = 'This permanently removes the case study.';
        ref.componentInstance.subject = project.title;
        ref.componentInstance.consequence =
            'It disappears from the home page carousel and the solution pages.';
        ref.componentInstance.confirmLabel = 'Delete';
        ref.result.then(
            () => {
                this.projectsService.delete(project.id).subscribe({
                    next: () => {
                        this.toastr.success(`${project.title} deleted`);
                        this.load();
                    },
                    error: (err) => this.toastr.error(this.messageFor(err, 'Unable to delete this project')),
                });
            },
            () => { /* dismissed */ },
        );
    }

    imagePending(project: AdminProject): boolean {
        return !this.settled.has(project.id);
    }

    imageBroken(project: AdminProject): boolean {
        return this.failedImages.has(project.id);
    }

    onImageLoad(project: AdminProject): void {
        this.settled.add(project.id);
    }

    onImageError(project: AdminProject): void {
        this.settled.add(project.id);
        this.failedImages.add(project.id);
    }

    /** Surfaces the server's own message (a rejected image says why) and falls
     *  back to a generic one. HttpServiceBase.handleError rethrows
     *  { httpError, service }, hence the outer unwrap. */
    private messageFor(err: any, fallback: string): string {
        return err?.httpError?.error?.message || err?.error?.message || fallback;
    }
}
